import React, { useState } from 'react';
import { Box, InputBase, Tooltip } from '@mui/material';
import {
  AddCommentOutlined,
  SendOutlined,
  TipsAndUpdatesOutlined,
} from '@mui/icons-material';
import { CustomLoadingButton } from 'components/Buttons/CustomLoadingButton/CustomLoadingButton';
import useAppTheme from 'hooks/useAppTheme';
import { useChatbotContext } from '../context/ChatbotContext';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';
import { useTranslation } from 'react-i18next';

interface ChatInputProps {
  onSubmit: (question: string) => void;
  onToggleSuggestions: () => void;
  handleNewChat: () => void;
  chatStarted: boolean;
  loading: boolean;
  showPersistentSuggestions: boolean;
}

export const ChatInput: React.FC<ChatInputProps> = ({
  onSubmit,
  onToggleSuggestions,
  handleNewChat,
  chatStarted,
  loading,
  showPersistentSuggestions,
}) => {
  const { t } = useTranslation();
  const [inputValue, setInputValue] = useState('');
  const { enableDarkTheme } = useAppTheme();
  const { isExpanded } = useChatbotContext();

  const colorMode = enableDarkTheme ? 'dark' : 'light';
  const themeColors = clientThemePalette[CLIENT_THEME][colorMode];
  const accentColor = themeColors.primary;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = inputValue.trim();
    if (trimmed) {
      onSubmit(trimmed);
      setInputValue('');
    }
  };

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      sx={{
        display: 'flex',
        gap: isExpanded ? 2 : 1,
        alignItems: 'flex-end',
        px: isExpanded ? 2 : 1,
        py: isExpanded ? 1.5 : 1,
        color: enableDarkTheme ? '#F3F5FF' : '#1E293B',
        overflow: 'hidden',
        width: '100%',
      }}
    >
      {chatStarted && (
        <Box>
          <Tooltip title={t('new_chat')} placement="top" arrow>
            <CustomLoadingButton
              onClick={handleNewChat}
              disabled={!chatStarted}
              size="small"
              sx={{
                p: isExpanded ? 1 : 0.6,
                borderRadius: '12px',
                minWidth: '0px',
                transform: 'rotate(180deg)',
                color: '#F3F5FF',
              }}
            >
              <AddCommentOutlined sx={{ transform: 'scaleY(-1)' }} />
            </CustomLoadingButton>
          </Tooltip>
        </Box>
      )}

      <Box>
        <Tooltip
          title={
            showPersistentSuggestions
              ? 'Disable Suggestions'
              : 'Enable Suggestion'
          }
          placement="top"
          arrow
        >
          <CustomLoadingButton
            onClick={onToggleSuggestions}
            size="small"
            sx={{
              p: isExpanded ? 1 : 0.6,
              borderRadius: '12px',
              minWidth: '0px',
            }}
          >
            <TipsAndUpdatesOutlined />
          </CustomLoadingButton>
        </Tooltip>
      </Box>

      <InputBase
        placeholder={t('ask_anything')}
        value={inputValue}
        onChange={(e) => {
          setInputValue(e.target.value);
        }}
        autoFocus
        multiline
        minRows={1}
        maxRows={6}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSubmit(e as any);
          }
        }}
        sx={{
          flex: 1,
          backgroundColor: enableDarkTheme
            ? `${themeColors.navbarBg}cc`
            : '#F7F9FC',
          border: `1.5px solid ${accentColor}44`,
          borderRadius: '12px',
          px: isExpanded ? 2 : 1,
          py: isExpanded ? 1 : 0.8,
          fontSize: isExpanded ? '16px' : '14px',
          lineHeight: 1.3,
          color: enableDarkTheme ? '#F3F5FF' : '#1E293B',
          transition: 'all 0.2s ease',
          '&:focus-within': {
            borderColor: accentColor,
            boxShadow: `0 0 0 3px ${accentColor}25`,
          },
          '& .MuiInputBase-input::placeholder': {
            color: enableDarkTheme ? '#6b7a8d' : '#94a3b8',
            opacity: 1,
          },
        }}
      />

      <Box>
        <CustomLoadingButton
          type="submit"
          sx={{
            p: isExpanded ? 1 : 0.9,
            borderRadius: '12px',
            minWidth: 0,
            background:
              inputValue?.trim()?.length > 0 && !loading
                ? `linear-gradient(135deg, ${accentColor}, ${accentColor}cc)`
                : undefined,
            '&:not(:disabled)': {
              boxShadow: `0 2px 10px ${accentColor}44`,
            },
          }}
          disabled={inputValue?.trim()?.length === 0 || loading}
          loading={loading}
          size="small"
        >
          <SendOutlined fontSize={isExpanded ? 'medium' : 'small'} />
        </CustomLoadingButton>
      </Box>
    </Box>
  );
};
